import React, { useState } from 'react';
import { useUploadContext, UploadBatch } from '../context/UploadContext';

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const getBatchProgress = (batch: UploadBatch): number => {
  let total = 0, uploaded = 0;
  batch.fileProgress.forEach(({ state }) => {
    total += state.totalBytes;
    uploaded += state.uploadedBytes;
  });
  if (total === 0) {
    return batch.totalFiles > 0 ? (batch.completedCount / batch.totalFiles) * 100 : 0;
  }
  return (uploaded / total) * 100;
};

const getBatchUploadedBytes = (batch: UploadBatch): number => {
  let uploaded = 0;
  batch.fileProgress.forEach(({ state }) => { uploaded += state.uploadedBytes; });
  return uploaded;
};

const getBatchTotalBytes = (batch: UploadBatch): number => {
  let total = 0;
  batch.fileProgress.forEach(({ state }) => { total += state.totalBytes; });
  return total;
};

const UploadProgressBanner: React.FC = () => {
  const { batches, cancelBatch, dismissBatch } = useUploadContext();
  const [isMinimised, setIsMinimised] = useState(false);

  if (batches.length === 0) return null;

  const activeCount = batches.filter(b => b.isUploading).length;

  return (
    <div style={{
      position: 'fixed',
      bottom: '1.5rem',
      right: '1.5rem',
      width: '340px',
      backgroundColor: 'white',
      borderRadius: '10px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
      border: '1px solid #e5e7eb',
      zIndex: 9999,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.6rem 0.875rem',
          backgroundColor: '#667eea',
          color: 'white',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setIsMinimised(v => !v)}
      >
        <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {activeCount > 0 ? `Uploading (${activeCount} active)` : 'Uploads'}
        </span>
        <span style={{ fontSize: '0.75rem', opacity: 0.85 }}>
          {isMinimised ? '▲' : '▼'}
        </span>
      </div>

      {!isMinimised && (
        <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
          {batches.map(batch => {
            const progress = getBatchProgress(batch);
            const uploadedBytes = getBatchUploadedBytes(batch);
            const totalBytes = getBatchTotalBytes(batch);
            const showBytes = totalBytes > 0;

            return (
              <div key={batch.id} style={{
                padding: '0.75rem 0.875rem',
                borderBottom: '1px solid #f3f4f6',
              }}>
                {/* Row: bucket name + action button */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.35rem' }}>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#374151', flex: 1, marginRight: '0.5rem', wordBreak: 'break-all' }}>
                    {batch.bucketDisplayName}
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    {batch.isUploading ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); cancelBatch(batch.id); }}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '0.75rem', padding: '0 0.2rem' }}
                      >
                        Cancel
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); dismissBatch(batch.id); }}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '1.1rem', padding: '0 0.2rem', lineHeight: 1 }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>

                {/* Progress bar */}
                {batch.isUploading && (
                  <>
                    <div style={{ height: '4px', backgroundColor: '#e5e7eb', borderRadius: '2px', marginBottom: '0.3rem', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${progress}%`,
                        backgroundColor: '#667eea',
                        borderRadius: '2px',
                        transition: 'width 0.3s ease',
                      }} />
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {batch.completedCount} / {batch.totalFiles} files
                      {showBytes && ` · ${formatBytes(uploadedBytes)} / ${formatBytes(totalBytes)}`}
                      {' '}· {progress.toFixed(0)}%
                    </div>
                  </>
                )}

                {/* Success */}
                {!batch.isUploading && !batch.error && (
                  <div style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 500 }}>
                    {batch.completedCount} file(s) uploaded successfully
                  </div>
                )}

                {/* Error */}
                {batch.error && (
                  <div style={{ fontSize: '0.75rem', color: '#ef4444' }}>{batch.error}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UploadProgressBanner;
